import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { LabSettingsView, LabValueSet } from '@wodi/shared';
import { Button, Checkbox, Notice, RadioGroup, Section, Tag, TextField } from '@wodi/ui';
import { useState } from 'react';

import {
  addLabSection,
  addLabValueSetConcept,
  labKeys,
  labSectionsQuery,
  labSettingsQuery,
  labValueSetQuery,
  putLabSettings,
  updateLabSection,
  updateLabValueSetConcept,
} from '../api/lab.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../lab/lab.module.css';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { LabSettingsFrame, QueryState } from './settings-lab.tsx';

// The lab's own lists (plan §2.1, C12): sections, specimen types, containers and rejection
// reasons. No national list is loaded (S2), so every entry is local and marked provisional.

const LISTS: { valueSet: LabValueSet; title: string; one: string }[] = [
  { valueSet: 'lab_specimen_type', title: 'Specimen types', one: 'specimen type' },
  { valueSet: 'lab_container', title: 'Containers', one: 'container' },
  { valueSet: 'lab_rejection_reason', title: 'Sample rejection reasons', one: 'rejection reason' },
];

export function LabListsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const canEdit = session !== null && can(session, 'lab.catalogue.manage');
  return (
    <LabSettingsFrame title="Lab">
      <Sections canEdit={canEdit} />
      {LISTS.map((list) => (
        <ValueSetList key={list.valueSet} {...list} canEdit={canEdit} />
      ))}
    </LabSettingsFrame>
  );
}

function Sections({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const sections = useQuery(labSectionsQuery);
  const [name, setName] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: labKeys.catalogue });
  const add = useMutation({
    mutationFn: () => addLabSection(name.trim(), (sections.data?.length ?? 0) + 1),
    onSuccess: async () => {
      toasts.show({ title: 'Section added' });
      setName('');
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      updateLabSection(id, { status: active ? 'active' : 'inactive' }),
    onSuccess: refresh,
  });
  return (
    <Section title="Sections">
      <p className={styles.meta}>The worklist groups work by section, for example Haematology.</p>
      <QueryState query={sections} what="sections" />
      {sections.isSuccess && sections.data.length === 0 ? (
        <p>No sections yet. Add one before setting up tests.</p>
      ) : null}
      <ul className={styles.list}>
        {(sections.data ?? []).map((s) => (
          <li key={s.id} className={styles.listRow}>
            <span>{s.name}</span>
            <span />
            <Checkbox
              isSelected={s.status === 'active'}
              isDisabled={!canEdit || !online}
              onChange={(active) => {
                toggle.mutate({ id: s.id, active });
              }}
            >
              {`${s.name} in use`}
            </Checkbox>
          </li>
        ))}
      </ul>
      {canEdit ? (
        <form
          className={styles.toolbar}
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() !== '') add.mutate();
          }}
        >
          <TextField label="New section" value={name} onChange={setName} />
          <Button
            type="submit"
            isDisabled={!online || name.trim() === ''}
            isPending={add.isPending}
            pendingLabel="Adding…"
          >
            Add
          </Button>
        </form>
      ) : null}
      {add.isError || toggle.isError ? (
        <Notice tone="error">
          {errorText(add.error ?? toggle.error, "The section wasn't saved. Try again.")}
        </Notice>
      ) : null}
    </Section>
  );
}

function ValueSetList({
  valueSet,
  title,
  one,
  canEdit,
}: {
  valueSet: LabValueSet;
  title: string;
  one: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const concepts = useQuery(labValueSetQuery(valueSet));
  const [display, setDisplay] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: labKeys.catalogue });
  const add = useMutation({
    mutationFn: () =>
      addLabValueSetConcept(valueSet, display.trim(), (concepts.data?.length ?? 0) + 1),
    onSuccess: async () => {
      toasts.show({ title: `Added to ${title.toLowerCase()}` });
      setDisplay('');
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: ({ code, active }: { code: string; active: boolean }) =>
      updateLabValueSetConcept(valueSet, code, { status: active ? 'active' : 'inactive' }),
    onSuccess: refresh,
  });
  const id = `vs-${valueSet}`;
  return (
    <section className={styles.section} aria-labelledby={id}>
      <h2 id={id} className={styles.sectionHeading}>
        {title}
      </h2>
      <QueryState query={concepts} what={title.toLowerCase()} />
      {concepts.isSuccess && concepts.data.length === 0 ? (
        <p>{`No ${title.toLowerCase()} yet.`}</p>
      ) : null}
      <ul className={styles.list}>
        {(concepts.data ?? []).map((c) => (
          <li key={c.code} className={styles.listRow}>
            <span>{c.display}</span>
            <span>{c.provisional ? <Tag state="pending">Provisional</Tag> : null}</span>
            <Checkbox
              isSelected={c.status === 'active'}
              isDisabled={!canEdit || !online}
              onChange={(active) => {
                toggle.mutate({ code: c.code, active });
              }}
            >
              {`${c.display} in use`}
            </Checkbox>
          </li>
        ))}
      </ul>
      {canEdit ? (
        <form
          className={styles.toolbar}
          onSubmit={(event) => {
            event.preventDefault();
            if (display.trim() !== '') add.mutate();
          }}
        >
          <TextField label={`New ${one}`} value={display} onChange={setDisplay} />
          <Button
            type="submit"
            isDisabled={!online || display.trim() === ''}
            isPending={add.isPending}
            pendingLabel="Adding…"
          >
            Add
          </Button>
        </form>
      ) : null}
      {add.isError || toggle.isError ? (
        <Notice tone="error">
          {errorText(add.error ?? toggle.error, "The list wasn't saved. Try again.")}
        </Notice>
      ) : null}
    </section>
  );
}

/** Per facility (plan §2.9). Escalation minutes stay empty until the clinical lead sets them (C6). */
export function LabFacilitySettingsPage() {
  const settings = useQuery(labSettingsQuery);
  return (
    <LabSettingsFrame title="Lab">
      <QueryState query={settings} what="lab settings" />
      {settings.isSuccess ? <SettingsForm settings={settings.data} /> : null}
    </LabSettingsFrame>
  );
}

function SettingsForm({ settings }: { settings: LabSettingsView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const [draft, setDraft] = useState(settings);
  const [copies, setCopies] = useState(String(settings.labelCopies));
  const [minutes, setMinutes] = useState(
    settings.criticalEscalationMinutes === null ? '' : String(settings.criticalEscalationMinutes),
  );
  const canEdit = session !== null && can(session, 'lab.settings.manage');
  const copiesNumber = Number(copies);
  const copiesError =
    !Number.isInteger(copiesNumber) || copiesNumber < 1 || copiesNumber > 5
      ? 'Enter 1 to 5 copies.'
      : undefined;
  const minutesNumber = minutes.trim() === '' ? null : Number(minutes);
  const minutesError =
    minutesNumber !== null &&
    (!Number.isInteger(minutesNumber) || minutesNumber < 1 || minutesNumber > 1440)
      ? 'Enter 1 to 1440 minutes, or leave empty.'
      : undefined;
  const save = useMutation({
    mutationFn: () =>
      putLabSettings({
        ...draft,
        labelCopies: copiesNumber,
        criticalEscalationMinutes: minutesNumber,
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Lab settings saved' });
      await queryClient.invalidateQueries({ queryKey: labKeys.all });
    },
  });
  const set = (patch: Partial<LabSettingsView>) => {
    setDraft((d) => ({ ...d, ...patch }));
  };
  return (
    <form
      className={styles.form}
      aria-label="Lab settings"
      onSubmit={(event) => {
        event.preventDefault();
        if (copiesError === undefined && minutesError === undefined) save.mutate();
      }}
    >
      <p className={styles.meta}>For {session?.facility.name ?? 'this facility'}.</p>
      <RadioGroup
        label="Validation"
        value={draft.validationStages}
        isDisabled={!canEdit}
        options={[
          {
            id: 'one',
            label: 'One stage',
            description: 'A senior technologist validates and releases.',
          },
          {
            id: 'two',
            label: 'Two stages',
            description:
              'Technical validation, then clinical validation by a pathologist or lab manager.',
          },
        ]}
        onChange={(v) => {
          set({ validationStages: v as 'one' | 'two' });
        }}
      />
      <Checkbox
        isSelected={draft.validatorMustDiffer}
        isDisabled={!canEdit}
        onChange={(validatorMustDiffer) => {
          set({ validatorMustDiffer });
        }}
        description="Switch off only in a lab with a single technologist. The change is audited."
      >
        Someone other than the person who entered results must validate them
      </Checkbox>
      <Checkbox
        isSelected={draft.useReceiveStep}
        isDisabled={!canEdit}
        onChange={(useReceiveStep) => {
          set({ useReceiveStep });
        }}
        description="For samples collected outside the lab and brought in."
      >
        Receive samples in the lab as a separate step
      </Checkbox>
      <TextField
        label="Label copies per sample"
        value={copies}
        onChange={setCopies}
        isDisabled={!canEdit}
        {...(copiesError === undefined ? {} : { errorMessage: copiesError })}
      />
      <RadioGroup
        label="Report paper"
        value={draft.reportPaper}
        isDisabled={!canEdit}
        options={[
          { id: 'a4', label: 'A4' },
          { id: 'a5', label: 'A5' },
        ]}
        onChange={(v) => {
          set({ reportPaper: v as 'a4' | 'a5' });
        }}
      />
      <TextField
        label="Escalate an unacknowledged critical result after"
        unit="min"
        description="Set by the clinical lead. While empty, a critical result escalates to the lab in-charge at once."
        value={minutes}
        onChange={setMinutes}
        isDisabled={!canEdit}
        {...(minutesError === undefined ? {} : { errorMessage: minutesError })}
      />
      {save.isError ? (
        <Notice tone="error">{errorText(save.error, "The lab settings weren't saved.")}</Notice>
      ) : null}
      {canEdit ? (
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isPending={save.isPending}
            pendingLabel="Saving…"
            isDisabled={!online}
          >
            Save settings
          </Button>
        </div>
      ) : (
        <Notice tone="info">Only the lab manager can change these settings.</Notice>
      )}
    </form>
  );
}
